import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { FileText, AlertCircle } from 'lucide-react';
import TestDiscForm from '../../components/test/TestDiscForm';
import Loader from '../../components/ui/Loader';
import Card from '../../components/ui/Card';
import testDiscService from '../../services/testDiscService';
import useAuthStore from '../../store/authStore';
import PantallaInstruccionesDisc from '../../components/exam/PantallaInstruccionesDisc';

/**
 * Página para realizar el Test DISC
 * Puede usarse de dos formas:
 * 1. Como ruta con params: /postulante/test-disc/:procesoId/:perfilId
 * 2. Como componente con props: <RealizarTestDisc discExamen={...} proceso={...} onFinalizar={...} />
 */
export default function RealizarTestDisc({ discExamen, proceso, onFinalizar }) {
  const { procesoId: routeProcesoId, perfilId: routePerfilId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [iniciando, setIniciando] = useState(true);
  const [mostrarPantallasInicio, setMostrarPantallasInicio] = useState(true);
  const [testIniciado, setTestIniciado] = useState(false);

  // Usar props o params de la ruta
  const procesoId = proceso?.id || routeProcesoId;
  const perfilId = routePerfilId;

  useEffect(() => {
    // Simular carga inicial
    setTimeout(() => setIniciando(false), 500);
  }, []);

  const handleSubmitTest = async (respuestas) => {
    try {
      setLoading(true);
      toast.loading('Procesando tu test DISC...', { id: 'test-disc' });

      const testData = {
        usuarioId: user.id,
        procesoId: parseInt(procesoId),
        respuestasMas: respuestas.respuestasMas,
        respuestasMenos: respuestas.respuestasMenos
      };

      console.log('📤 Enviando test DISC:', testData);

      const resultado = await testDiscService.procesarTest(testData);

      console.log('✅ Test DISC procesado:', resultado);

      toast.dismiss('test-disc');
      toast.success('¡Test DISC completado exitosamente!', { duration: 5000 });

      // Mostrar resultado resumido
      if (resultado.perfilDominante) {
        toast.success(
          `Tu perfil dominante es: ${resultado.perfilDominante}`,
          { duration: 7000, icon: '🎯' }
        );
      }

      // Si hay callback, usarlo; sino, navegar
      if (onFinalizar) {
        setTimeout(() => {
          onFinalizar();
        }, 2000);
      } else {
        setTimeout(() => {
          navigate('/postulante/mis-tests');
        }, 2000);
      }

    } catch (error) {
      toast.dismiss('test-disc');
      console.error('❌ Error al procesar test DISC:', error);
      
      const mensajeError = error.response?.data?.mensaje || 
                          error.response?.data?.message || 
                          'Error al procesar el test. Por favor, intenta nuevamente.';
      
      toast.error(mensajeError, { duration: 7000 });
    } finally {
      setLoading(false);
    }
  };

  if (iniciando) {
    return <Loader />;
  }

  // Pantalla de instrucciones DISC
  if (mostrarPantallasInicio) {
    return (
      <PantallaInstruccionesDisc
        onIniciar={() => {
          setMostrarPantallasInicio(false);
          setTestIniciado(true);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-primary-100 rounded-full">
              <FileText className="w-12 h-12 text-primary" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-neutral-800">Test DISC</h1>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            El test DISC evalúa tu estilo de comportamiento y personalidad. 
            Responde con honestidad, no hay respuestas correctas o incorrectas.
          </p>
        </div>

        {/* Instrucciones */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <div className="flex gap-4">
            <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="space-y-2">
              <h3 className="font-semibold text-blue-900">Instrucciones importantes:</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>El test consta de <strong>28 grupos de características</strong></li>
                <li>En cada grupo, selecciona la característica que <strong className="text-green-700">MÁS te describe</strong></li>
                <li>Luego, selecciona la que <strong className="text-red-700">MENOS te describe</strong></li>
                <li>No puedes seleccionar la misma característica para ambas opciones</li>
                <li>Responde con sinceridad basándote en cómo eres realmente, no en cómo te gustaría ser</li>
                <li>El test se guardará automáticamente al finalizar</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Formulario del test */}
        <TestDiscForm
          onSubmit={handleSubmitTest}
          loading={loading}
        />

        {/* Footer informativo */}
        <div className="text-center text-sm text-neutral-500 pb-8">
          <p>
            Este test evalúa cuatro dimensiones: <strong>Dominancia (D)</strong>, <strong>Influencia (I)</strong>, 
            <strong> Estabilidad (S)</strong> y <strong>Cumplimiento (C)</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
